import {
  updateDoc,
  doc,
  getDoc,
  setDoc,
  getFirestore,
} from 'firebase/firestore';

import {
  ref,
  uploadString,
  getMetadata,
  deleteObject,
  getStorage,
} from 'firebase/storage';

import { getApp } from 'firebase/app';
import type { MessageApi } from 'ant-design-vue/es/message';
import type { Image } from './types';
import type { UploadFile } from 'ant-design-vue';

function hexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    red: parseInt(result[1] ?? '00', 16),
    green: parseInt(result[2] ?? '00', 16),
    blue: parseInt(result[3] ?? '00', 16),
  } : null;
}

export async function uploadImage(
  image: Image,
  path: string,
  type: 'logo' | 'logodark' | 'backgroundImage' | 'favicon',
  message: MessageApi,
): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  let checkType1 = 'image/x-icon';
  let checkType2 = 'image/vnd.microsoft.icon';
  let checkString = 'You can only upload ICO files!';
  if (type != 'favicon') {
    checkType1 = 'image/jpeg';
    checkType2 = 'image/png';
    checkString = 'You can only upload JPG or PNG files!';
  }

  const isJpgOrPng = image.type === checkType1 || image.type === checkType2;
  if (!isJpgOrPng) {
    message.error(checkString);
    return false;
  }
  const isLt2M = image.size / 1024 / 1024 <= 1;
  if (!isLt2M) {
    message.error('Image must smaller than 1MB!');
    return false;
  }

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const storageRef = ref(storage, 'teams/' + this.team + path + image.name);

      const metadata = {
        contentType: image.type,
        cacheControl: 'public,max-age=1800',
      };

      try {
        const result = await uploadString(storageRef, image.data, 'data_url', metadata);

        // store result path to user object
        const docRef = doc(db, 'teams', this.team);
        await updateDoc(docRef, {
          [type]: result.ref.fullPath,
        });

        return true;
      } catch (error) {
        console.warn(error);
        return false;
      }
    } else {
      message.error('You do not have permission to perform this operation!');
      return false;
    }
  } else {
    message.error('You do not have permission to perform this operation!');
    return false;
  }
}

export async function fetchImage(type: 'logo' | 'logodark' | 'backgroundImage' | 'favicon') {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.team != null && this.userMode == 'team') {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      if (docSnap.data()[type] != null) {
        const image= docSnap.data()[type];
        const imageRef = ref(storage, image);
        const imageMeta = await getMetadata(imageRef);

        const imageObject = {
          uid: '-1',
          name: imageMeta.name,
          status: 'done',
          url: import.meta.env.VITE_CDN_URL + docSnap.data()[type],
        } as UploadFile;

        return imageObject;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function clearImage(type: 'logo' | 'logodark' | 'backgroundImage' | 'favicon') {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      if (docSnap.data()[type] != null) {
        const image = docSnap.data()[type];
        const imageRef = ref(storage, image);
        await deleteObject(imageRef);
        await updateDoc(docRef, {
          [type]: null,
        });
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function getSolidHeader() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      if (docSnap.data().solidHeader == null) {
        return false;
      } else {
        return docSnap.data().solidHeader;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function setSolidHeader(solidHeader: string[]): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      try {
        await updateDoc(docRef, {
          'solidHeader': solidHeader,
        });
        return true;
      } catch (error) {
        console.warn(error);
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function getColorPreferences() {
  try {
    const fbApp = getApp(this.firebaseApp);
    const db = getFirestore(fbApp, this.dbid);

    if (this.team != null && this.userMode == 'team') {
      const docRef = doc(db, 'teams', this.team);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        if (
          this.colors != null &&
          this.colors.timestamp != null &&
          Date.now() - this.colors.timestamp < 1800000 &&
          this.colors.teamId == this.team
        ) {
          const primaryColors = this.colors.primary;
          const bgColors = this.colors.loginBackground;
          const primary = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
          const loginBackground = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;

          const rootElement = document.querySelector<HTMLElement>(':root');
          if (rootElement) {
            rootElement.style.setProperty('--primary-color', primary);
            rootElement.style.setProperty('--login-background-color', loginBackground);
          }
          return;
        } else {
          if (docSnap.data().colors != null && docSnap.data().colors.primary && docSnap.data().colors.loginBackground) {
            const dbColors = docSnap.data().colors;
            this.colors = {
              primary: dbColors.primary,
              loginBackground: dbColors.loginBackground,
              timestamp: Date.now(),
              teamId: this.team,
            };
            const primaryColors = this.colors.primary;
            const bgColors = this.colors.loginBackground;
            const primary = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
            const loginBackground = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;

            const rootElement = document.querySelector<HTMLElement>(':root');
            if (rootElement) {
              rootElement.style.setProperty('--primary-color', primary);
              rootElement.style.setProperty('--login-background-color', loginBackground);
            }
          } else {
            this.resetColorPreferences();
            const primaryColors = this.colors.primary;
            const bgColors = this.colors.loginBackground;
            const primary = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
            const loginBackground = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;

            const rootElement = document.querySelector<HTMLElement>(':root');
            if (rootElement) {
              rootElement.style.setProperty('--primary-color', primary);
              rootElement.style.setProperty('--login-background-color', loginBackground);
            }
          }
        }
      }
    } else {
      this.resetColorPreferences();
      const primaryColors = this.colors.primary;
      const bgColors = this.colors.loginBackground;
      const primary = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
      const loginBackground = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;

      const rootElement = document.querySelector<HTMLElement>(':root');
      if (rootElement) {
        rootElement.style.setProperty('--primary-color', primary);
        rootElement.style.setProperty('--login-background-color', loginBackground);
      }
    }
  } catch {
    this.resetColorPreferences();
    const primaryColors = this.colors.primary;
    const bgColors = this.colors.loginBackground;
    const primary = primaryColors.red + ' ' + primaryColors.green + ' ' + primaryColors.blue;
    const loginBackground = bgColors.red + ' ' + bgColors.green + ' ' + bgColors.blue;

    const rootElement = document.querySelector<HTMLElement>(':root');
    if (rootElement) {
      rootElement.style.setProperty('--primary-color', primary);
      rootElement.style.setProperty('--login-background-color', loginBackground);
    }
  }
}

export async function updateColorPreferences(
  colors: { primaryHex: string, loginBackgroundHex: string },
): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const primary = hexToRgb(colors.primaryHex) || this.colors.primary;
      const loginBackground = hexToRgb(colors.loginBackgroundHex) || this.colors.loginBackground;

      const primaryString = primary.red + ' ' + primary.green + ' ' + primary.blue;
      const loginBackgroundString = loginBackground.red + ' ' + loginBackground.green + ' ' + loginBackground.blue;

      const rootElement = document.querySelector<HTMLElement>(':root');
      if (rootElement) {
        rootElement.style.setProperty('--primary-color', primaryString);
        rootElement.style.setProperty('--login-background-color', loginBackgroundString);
      }

      const colorsNew = {
        primary: primary,
        loginBackground: loginBackground,
      };

      this.colors = colorsNew;

      if (docSnap.exists()) {
        try {
          await updateDoc(docRef, {
            colors: colorsNew,
          });

          return true;
        } catch {
          return false;
        }
      } else {
        try {
          await setDoc(doc(db, 'teams', this.team), {
            colors: colorsNew,
          });
          return true;
        } catch {
          return false;
        }
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function uploadLogo(
  { image, message }:
  { image: Image, message: MessageApi },
) {
  return await this.uploadImage(image, '/images/logo/', 'logo', message);
}

export async function uploadLogoDark(
  { image, message }:
  { image: Image, message: MessageApi },
) {
  return await this.uploadImage(image, '/images/darklogo/', 'logodark', message);
}

export async function uploadBackground(
  { image, message }:
  { image: Image, message: MessageApi },
) {
  return await this.uploadImage(image, '/images/background/', 'backgroundImage', message);
}

export async function uploadFavicon(
  { image, message }:
  { image: Image, message: MessageApi },
) {
  return await this.uploadImage(image, '/images/favicon/', 'favicon', message);
}

export async function getLogo() {
  return await this.fetchImage('logo');
}

export async function getLogoDark() {
  return await this.fetchImage('logodark');
}

export async function getBackgroundImage() {
  return await this.fetchImage('backgroundImage');
}

export async function getFavicon() {
  return await this.fetchImage('favicon');
}

export async function clearLogo() {
  return await this.clearImage('logo');
}

export async function clearLogoDark() {
  return await this.clearImage('logodark');
}

export async function clearBackgroundImage() {
  return await this.clearImage('backgroundImage');
}

export async function clearFavicon() {
  return await this.clearImage('favicon');
}
